import * as React from 'react';

// Data modes
export const DATA_MODES = {
  DEMO: 'demo',   // Static data, sessionStorage, resets on reload
  LIVE: 'live',   // Firebase data, persists across sessions
};

const DataModeContext = React.createContext(null);

export function DataModeProvider({ mode = DATA_MODES.DEMO, children }) {
  const isDemo = mode === DATA_MODES.DEMO;
  const isLive = mode === DATA_MODES.LIVE;

  // Storage key prefix based on mode
  const storagePrefix = isDemo ? 'demo_' : 'live_';

  // Get storage (sessionStorage for demo, localStorage for live)
  const getStorage = () => {
    return isDemo ? sessionStorage : localStorage;
  };

  // Load data from appropriate storage with basic type validation
  const loadData = (key, defaultValue) => {
    try {
      const storage = getStorage();
      const stored = storage.getItem(storagePrefix + key);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Basic type validation: ensure parsed type matches default
        if (defaultValue !== undefined && defaultValue !== null) {
          if (Array.isArray(defaultValue) && !Array.isArray(parsed)) return defaultValue;
          if (typeof parsed !== typeof defaultValue) return defaultValue;
        }
        return parsed;
      }
    } catch (e) {
      console.error('Error loading from storage:', e);
    }
    return defaultValue;
  };

  // Save data to appropriate storage
  const saveData = (key, value) => {
    try {
      const storage = getStorage();
      storage.setItem(storagePrefix + key, JSON.stringify(value));
    } catch (e) {
      console.error('Error saving to storage:', e);
    }
  };

  // Clear all data for current mode
  const clearData = () => {
    try {
      const storage = getStorage();
      const keysToRemove = [];
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key && key.startsWith(storagePrefix)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => storage.removeItem(key));
    } catch (e) {
      console.error('Error clearing storage:', e);
    }
  };

  const value = {
    mode,
    isDemo,
    isLive,
    storagePrefix,
    loadData,
    saveData,
    clearData,
    getStorage,
  };

  return (
    <DataModeContext.Provider value={value}>
      {children}
    </DataModeContext.Provider>
  );
}

export function useDataMode() {
  const context = React.useContext(DataModeContext);
  // Return null if not wrapped in DataModeProvider (graceful fallback)
  return context;
}

export default DataModeContext;
